import { useState } from "react";
import { implementationPath } from "@/data/elevateImplementationPath";

const CORE_CHOICES = [
  {
    name: "ChatGPT",
    strengths: "Draft from supplied documents and analyze spreadsheets with calculations you can inspect.",
    weaknesses: "Available tools and connected sources depend on the approved workspace and plan.",
    opportunities: "Test a priority brief, reply queue, or variance report against your current process.",
    threats: "A plausible summary can hide a missing source or incorrect calculation. Verify both.",
    source: "https://help.openai.com/en/articles/9260256-chatgpt-capabilities-overview",
  },
  {
    name: "Claude",
    strengths: "Keep approved files and instructions in a Project for repeated drafting and analysis.",
    weaknesses: "Project context must be maintained; browser work and scheduling need their own supported setup.",
    opportunities: "Test a learning packet or reusable reporting method, then add reviewed corrections.",
    threats: "Outdated project files or excessive tool access can undermine a good prompt. Check the boundary.",
    source: "https://support.claude.com/en/articles/9519177-how-can-i-create-and-manage-projects",
  },
];

const ROCKLINE_CHOICES = [
  {
    name: "Copilot Chat",
    strengths: "Start with an approved file and a clear question in the Rockline work account.",
    weaknesses: "Do not assume broad access to work data. Confirm the license, enabled sources, and agents.",
    opportunities: "Test a supplier-quality or inventory brief from approved exports before adding connections.",
    threats: "A file may be stale or incomplete. The owner checks the reporting period and source rows.",
    source: "https://learn.microsoft.com/en-us/copilot/faq",
  },
  {
    name: "Microsoft 365 Copilot",
    strengths: "Use permitted work content through the Microsoft 365 experiences enabled for your account.",
    weaknesses: "Work grounding and agent access depend on licensing, configuration, and existing permissions.",
    opportunities: "Test a brief from approved Outlook, Teams, or SharePoint sources when access is confirmed.",
    threats: "Existing access does not establish accuracy or authority to act. Verify sources and keep human approval.",
    source: "https://learn.microsoft.com/en-us/copilot/overview",
  },
];

export const ElevateSwotLibrary = ({ rockline }: { rockline: boolean }) => {
  const path = implementationPath(rockline);
  const choices = rockline ? ROCKLINE_CHOICES : CORE_CHOICES;
  const [selected, setSelected] = useState(0);
  const entry = choices[selected];
  return (
    <div className="mx-auto w-full max-w-6xl py-4 text-[#0A1F44]">
      <p className="text-xs font-bold uppercase tracking-widest text-[#0047FF]">Tool Decision · Compare against your Job</p>
      <h1 className="s2p-display my-3 text-3xl font-semibold sm:text-4xl">{path.title}</h1>
      <p className="text-base sm:text-lg">{path.target}</p>
      <div className="my-4 flex flex-wrap gap-3" role="group" aria-label="Choose a tool path">
        {choices.map((choice, index) => <button key={choice.name} type="button" aria-pressed={selected === index} onClick={() => setSelected(index)} className={`min-h-11 rounded border px-4 py-2 font-bold ${selected === index ? "border-[#0047FF] bg-[#0047FF] text-white" : "border-slate-400 bg-white text-[#0A1F44]"}`}>{choice.name}</button>)}
      </div>
      <div className="grid gap-3 sm:grid-cols-2" aria-label={`${entry.name} workflow fit`}>
        {(["strengths", "weaknesses", "opportunities", "threats"] as const).map((quadrant) => (
          <section key={quadrant} className="border border-slate-300 bg-white p-4">
            <h2 className="text-sm font-bold uppercase tracking-wide text-[#0047FF]">{quadrant}</h2>
            <p className="mt-2 text-base leading-snug">{entry[quadrant]}</p>
          </section>
        ))}
      </div>
      <p className="mt-4 font-semibold">Use the same Job, approved inputs, and review standard. What evidence would make you keep or change this choice?</p>
      <p className="mt-3 text-xs text-slate-600">Workshop interpretation, not measured rankings. Check the features available in your account. <a className="font-semibold text-[#0047FF] underline underline-offset-2" href={entry.source} target="_blank" rel="noopener noreferrer">{entry.name} source</a> · Reviewed September 25, 2026.</p>
    </div>
  );
};
